import React, { useState } from "react";
import { Box, Typography, Collapse, IconButton } from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { AccessibilitySection, DetailedNote, Note } from "@/types/common";

interface Props {
  data: AccessibilitySection[];
  noHeaderText?: boolean;
  optionTick?: boolean;
}

interface NoteItemProps {
  item: DetailedNote;
  noHeaderText?: boolean;
  optionTick?: boolean;
}

// Shared collapsible card wrapper used by both NoteItem and UserNoteItem
const CollapsibleNoteCard = ({ title, children }: { title: React.ReactNode; children: React.ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Box sx={{ mb: "5%" }}>
      <Box
        onClick={() => setIsOpen(!isOpen)}
        sx={{
          border: "1px solid #D9D9D9",
          borderRadius: "8px",
          padding: "4%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "pointer",
          backgroundColor: "#fff",
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: "3.5vw", sm: 14 },
            color: "#191919",
          }}
        >
          {title}
        </Typography>
        <IconButton size="small" sx={{ p: 0 }}>
          {isOpen ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>

      <Collapse in={isOpen}>
        <Box
          sx={{
            mt: "2%",
            border: "1px solid #D9D9D9",
            borderRadius: "8px",
            padding: "4%",
            backgroundColor: "#fff",
          }}
        >
          {children}
        </Box>
      </Collapse>
    </Box>
  );
};

const NoteItem = ({ item, noHeaderText, optionTick }: NoteItemProps) => {
  const { header_text, notes, is_correct } = item;

  return (
    <CollapsibleNoteCard title={header_text}>
      {is_correct === false && (
        <Typography sx={{ color: "#d32f2f", fontSize: "12px", fontWeight: "bold", mb: "2%" }}>
          Not Approved
        </Typography>
      )}
      {!noHeaderText && (
        <Typography
          sx={{
            fontSize: { xs: "3.16vw", sm: 13 },
            color: "#626262",
            mb: "3%",
          }}
        >
          All applicable options
        </Typography>
      )}

      <Box component="ul" sx={{ m: 0, pl: "5%", listStyleType: optionTick ? '"✔  "' : 'disc' }}>
        {notes?.map(({ note_text }: Note, j: number) => (
          <Box
            component="li"
            key={j}
            sx={{
              fontSize: { xs: "3.16vw", sm: 13 },
              color: "#424242",
              mb: "2%",
              "&::marker": {
                color: "#626262",
              },
            }}
          >
            {note_text}
          </Box>
        ))}
      </Box>
    </CollapsibleNoteCard>
  );
};

interface UserNoteItemProps {
  texts: string;
  optionTick?: boolean;
  is_correct?: boolean;
}

const UserNoteItem = ({ texts, optionTick, is_correct }: UserNoteItemProps) => {
  const notesList = texts?.split(",") || [];

  return (
    <CollapsibleNoteCard title="Other">
      {is_correct === false && (
        <Typography sx={{ color: "#d32f2f", fontSize: "12px", fontWeight: "bold", mb: "2%" }}>
          Not Approved
        </Typography>
      )}
      <Box component="ul" sx={{ m: 0, pl: "5%", listStyleType: optionTick ? '"✔  "' : 'disc' }}>
        {notesList?.map((note, k) => (
          <Box
            component="li"
            key={k}
            sx={{
              fontSize: { xs: "3.16vw", sm: 13 },
              color: "#424242",
              mb: "2%",
              "&::marker": {
                color: "#626262",
              },
            }}
          >
            {note.trim()}
          </Box>
        ))}
      </Box>
    </CollapsibleNoteCard>
  );
};

const NotesSection: React.FC<Props> = ({ data = [], noHeaderText, optionTick }) => {
  return (
    <Box>

      <Typography
        sx={{
          fontSize: { xs: "3.4vw", sm: 14 },
          fontWeight: 700,
          color: "#191919",
          mb: "3%",
        }}
      >
        Notes
      </Typography>


      {data?.length ? (
        data?.map(({ default_notes, user_notes }: AccessibilitySection, index: number) => (
          <Box key={index}>
            {default_notes?.map((item: DetailedNote, i: number) => (
              <NoteItem key={`${item.header_text}-${i}`} item={item} noHeaderText={noHeaderText} optionTick={optionTick} />
            ))}

            {user_notes?.text && <UserNoteItem texts={user_notes.text} optionTick={optionTick} is_correct={(user_notes as { is_correct?: boolean }).is_correct} />}
          </Box>
        ))
      ) : (
        <Typography
          sx={{
            fontSize: { xs: "2.91vw", sm: 12 },
            fontWeight: 700,
            color: "#191919",
          }}
        >
          No notes available.
        </Typography>
      )}
    </Box>
  );
};

export default NotesSection;
